/**
 * Helpers for the single-label classification confidence-threshold fallback
 * ("Other" / rejection label) selected during dataset label review.
 */

// Labels commonly used as a catch-all/fallback category. Matched
// case-insensitively against the final (post-mapping) label names.
const FALLBACK_LABEL_CANDIDATES = ['other', 'none', 'else', 'unknown'];

/**
 * Resolve the final (post-mapping) label name for an extracted label.
 *
 * Mirrors `ExtractedLabelItem`'s effective-label logic: a mapping value of
 * `null`/`undefined` (delete / identity) falls back to the raw label.
 *
 * @param {string} label
 * @param {Object} labelMappings - { extractedLabel: targetLabel | null }
 * @returns {string}
 */
export const resolveEffectiveLabel = (label, labelMappings = {}) => {
    const mapped = labelMappings?.[label];
    if (mapped === null || mapped === undefined || mapped === '') {
        return label;
    }
    return mapped;
};

/**
 * The unique set of final (post-mapping) label names for the dataset.
 *
 * @param {Array<string>} extractedLabels
 * @param {Object} labelMappings
 * @returns {Array<string>}
 */
export const getEffectiveLabels = (extractedLabels = [], labelMappings = {}) =>
    [
        ...new Set(
            (extractedLabels || []).map((label) =>
                resolveEffectiveLabel(label, labelMappings)
            )
        ),
    ].filter(Boolean);

/**
 * Detect an existing label that looks like a catch-all fallback
 * (Other / None / Else / Unknown), so we can suggest marking it as the
 * confidence-threshold fallback. Returns the first matching final label name,
 * or `null` when none is detected.
 *
 * @param {Array<string>} extractedLabels
 * @param {Object} labelMappings
 * @returns {string|null}
 */
export const detectFallbackLabel = (extractedLabels = [], labelMappings = {}) => {
    const effective = getEffectiveLabels(extractedLabels, labelMappings);
    for (const label of effective) {
        if (FALLBACK_LABEL_CANDIDATES.includes(String(label).trim().toLowerCase())) {
            return label;
        }
    }
    return null;
};
